/**
 * AlertServiceV2 - client for the standalone `alerting` service behind the gateway.
 *
 * Plain HttpClient rather than the hateoas client, because the alerting endpoints are not
 * network-scoped resource collections under /core: they name a network in the query or the
 * body, page on an opaque cursor, and half of them are actions. The contract details this
 * service protects callers from are paging by following a relative `next` link, one filter
 * object for the listing and the stats, a read timeout so a hung request cannot hold a
 * loading overlay up, a `sort` that is a first-page parameter only, and a `validate*` twin
 * for every write.
 */

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    AlertTypeView,
    ClaimRequest,
    AlertFilters,
    AlertPage,
    AlertSort,
    AlertStats,
    AlertTimeline,
    AlertView,
    DetectorView,
    ENVIRONMENT,
    Environment,
    LatencyPolicy,
    LatencyPolicyFilters,
    LatencyPolicyPage,
    LatencyPolicyKind,
    LatencyPolicyRequest,
    LatencyPolicySort,
    LatencyPolicyUpdate,
    OfflinePolicy,
    OfflinePolicyFilters,
    OfflinePolicyPage,
    OfflinePolicyRequest,
    OfflinePolicySort,
    OfflinePolicyUpdate,
    OfflineRungs,
    Mute,
    MuteFilters,
    MutePage,
    MuteRequest,
    MuteSort,
    MuteState,
    SortSpec,
    Timeline,
    ValidationError,
} from '@netfoundry-ui/shared/model';
import { HTTP_CLIENT } from '@netfoundry-ui/shared/services';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { get, isEmpty, isNil } from 'lodash';

/** Matches the other gateway-hosted services: /notification/v1, /identity/v1, ... */
const ALERTING_GATEWAY_PATH = '/alerting/v1';

/**
 * How long a read may take before it is treated as failed.
 *
 * Not a performance budget but a liveness bound: an HttpClient request that is never
 * answered neither emits nor completes, so the promise never settles and every `finally`
 * behind it never runs - which on the alerts page leaves the loading overlay up
 * permanently. Well above any healthy response, so this fires only when something is
 * genuinely wrong.
 */
const READ_TIMEOUT_MS = 30000;

/**
 * How many pages {@link AlertServiceV2.listMutes} will follow before it stops.
 *
 * A bound on a loop rather than a page budget: `listMutes` follows the `next` link to the
 * end, and a service that answered every page with a link to another would otherwise keep
 * it fetching forever. Far above any real mute set, so reaching it means something is wrong
 * rather than that somebody muted a lot.
 */
const MUTE_PAGE_LIMIT = 20;

/**
 * One time window as the comparisons the alerting service parses.
 *
 * Half open - `>=` on the start and `<` on the end - so two adjacent windows neither count
 * an alert on the boundary twice nor drop it, and every comparison in the set must hold, so
 * a pair narrows to the intersection.
 *
 * @param after the inclusive start, or undefined for no lower bound
 * @param before the exclusive end, or undefined for no upper bound
 */
function comparisons(after?: string, before?: string): string[] {
    const values: string[] = [];
    if (after) {
        values.push(`>=${after}`);
    }
    if (before) {
        values.push(`<${before}`);
    }
    return values;
}

/**
 * Adds `sort=<field>,<dir>` to a listing's query, when the caller asked for an order.
 *
 * Absent means absent, and so does an empty list, because every listing has a default order
 * of its own. Several terms are several parameters: `sort` repeats, most significant first,
 * and the service reads that order, so this may not sort or tidy the list it is given - and
 * only the first page needs it, since a `next` link carries the sort that made it, which is
 * why
 * {@link AlertServiceV2.listAlertsByHref} takes none.
 */
function withSort<F extends string>(params: HttpParams, sort?: SortSpec<F> | SortSpec<F>[]): HttpParams {
    if (!sort) {
        return params;
    }
    const terms = Array.isArray(sort) ? sort : [sort];
    return terms.reduce((carried, term) => carried.append('sort', `${term.field},${term.direction}`), params);
}

@Injectable({ providedIn: 'root' })
export class AlertServiceV2 {
    /** No trailing slash — every path below starts with one. */
    readonly apiUrl: string;

    constructor(@Inject(HTTP_CLIENT) private http: HttpClient, @Inject(ENVIRONMENT) private environment: Environment) {
        this.apiUrl = this.resolveApiUrl();
    }

    /**
     * `environment.alertingUrl` wins when set. Otherwise the url is derived from the
     * gateway this console already talks to, so an environment file only needs an entry
     * when its alerting service sits somewhere else.
     */
    private resolveApiUrl(): string {
        const configured = this.environment.alertingUrl;
        if (!isEmpty(configured)) {
            return configured.replace(/\/$/, '');
        }
        const protocol = this.environment.protocol || 'https';
        return `${protocol}://${this.environment.gatewayUrl}${ALERTING_GATEWAY_PATH}`;
    }

    /**
     * The first page of alerts for a network.
     *
     * Newest first unless `sort` says otherwise — the service defaults to
     * `firstAssertedAt,desc` when the parameter is absent, and this layer sends nothing
     * rather than spelling that default out.
     *
     * `sort` takes one term or several, most significant first — see {@link withSort}.
     */
    listAlertsPage(
        networkId: string,
        filters: AlertFilters = {},
        size?: number,
        sort?: AlertSort | AlertSort[]
    ): Promise<AlertPage> {
        let params = this.toParams(networkId, filters);
        if (!isNil(size)) {
            params = params.set('size', `${size}`);
        }
        params = withSort(params, sort);
        return firstValueFrom(this.http.get(`${this.apiUrl}/alerts`, { params }).pipe(timeout(READ_TIMEOUT_MS))).then(
            (body) => this.toPage(body)
        );
    }

    /**
     * The next page of alerts, from a `next` href handed back by a previous call.
     *
     * The href is resolved against the service base and otherwise used exactly as
     * received — see {@link resolveHref}. Rebuilding the query from parsed parts is the
     * one thing the contract asks callers not to do, and resolving a relative url is not
     * that: the cursor is carried across untouched.
     */
    listAlertsByHref(href: string): Promise<AlertPage> {
        return firstValueFrom(this.http.get(this.resolveHref(href)).pipe(timeout(READ_TIMEOUT_MS))).then((body) =>
            this.toPage(body)
        );
    }

    /**
     * Counts for the page header. Pass the same filters as the listing — that is the
     * whole point of the endpoint taking them.
     */
    getAlertStats(networkId: string, filters: AlertFilters = {}): Promise<AlertStats> {
        return firstValueFrom(
            this.http
                .get<AlertStats>(`${this.apiUrl}/alerts/stats`, { params: this.toParams(networkId, filters) })
                .pipe(timeout(READ_TIMEOUT_MS))
        );
    }

    /**
     * One alert by id — the same view a listing row carries (components resolved server-side).
     * For deep-linking a specific alert that may not be on the current page of the listing;
     * needs only `read` on the alert, the privilege that loads a row.
     */
    getAlert(alertId: string): Promise<AlertView> {
        return firstValueFrom(this.http.get<AlertView>(`${this.apiUrl}/alerts/${alertId}`).pipe(timeout(READ_TIMEOUT_MS)));
    }

    /**
     * The same counts over a window, cut into buckets - one point per bucket for a chart.
     *
     * The filters cut the same way the listing's do, so a `status` pinned to the open states
     * leaves out every outage that ended inside the window. `from` and `to` are required and
     * `to` is exclusive; `buckets` is a count rather than an interval, capped at 1 to 100.
     */
    getAlertTimeline(
        networkId: string,
        filters: AlertFilters = {},
        window: { from: string; to: string; buckets?: number }
    ): Promise<AlertTimeline> {
        let params = this.toParams(networkId, filters).set('from', window.from).set('to', window.to);
        if (!isNil(window.buckets)) {
            params = params.set('buckets', `${window.buckets}`);
        }
        return firstValueFrom(
            this.http.get<AlertTimeline>(`${this.apiUrl}/alerts/stats/timeline`, { params }).pipe(timeout(READ_TIMEOUT_MS))
        );
    }

    /**
     * What happened to one alert, oldest first.
     *
     * Transitions only, so the timeline is the length of the story rather than of the
     * outage - a firing alert is re-asserted every detector interval and none of those is an
     * event. `derived: true` means nothing was recorded and the timeline was reconstructed
     * from the alert document, which a view has to say - see {@link Timeline} - and it is
     * capped at 500 events with no paging, needing only `read` on the alert.
     */
    getAlertHistory(alertId: string): Promise<Timeline> {
        return firstValueFrom(
            this.http.get<Timeline>(`${this.apiUrl}/alerts/${alertId}/history`).pipe(timeout(READ_TIMEOUT_MS))
        ).then((body) => ({ events: body?.events || [], derived: body?.derived === true }));
    }

    /**
     * The alert types that can appear, and which detector produces each.
     *
     * Taken from the detectors rather than from a list here, because a hand-maintained list
     * drifts in the direction that hides things - a console filtering on a stale list never
     * offers the new type. `networkId` is required even though the answer does not depend on
     * it, because the health of an alerting service is not readable without a scope - and
     * `latencyPolicy` on each row says whether a latency policy means anything for that
     * type; see {@link AlertTypeView}.
     */
    listAlertTypes(networkId: string, alertTypes: string[] = []): Promise<AlertTypeView[]> {
        let params = new HttpParams().set('networkId', networkId);
        for (const alertType of alertTypes) {
            params = params.append('alertType', alertType);
        }
        return firstValueFrom(
            this.http.get(`${this.apiUrl}/alert-types`, { params }).pipe(timeout(READ_TIMEOUT_MS))
        ).then((body) => this.toCollection<AlertTypeView>(body, 'alertTypeViewList'));
    }

    /**
     * Detector health. This is what makes "no alerts" readable: a degraded detector
     * freezes the alerts it owns instead of resolving them, so an empty list beside a
     * DEGRADED detector means something different from an empty list with everything
     * healthy.
     */
    listDetectors(networkId: string): Promise<DetectorView[]> {
        const params = new HttpParams().set('networkId', networkId);
        return firstValueFrom(this.http.get(`${this.apiUrl}/detectors`, { params })).then((body) =>
            this.toCollection<DetectorView>(body, 'detectorViewList')
        );
    }

    /**
     * The mutes in one network, in one state - every page, not the first one.
     *
     * For the mute behind one alert, read `AlertPage.mutes` instead, because an alert
     * response embeds it; this answers the wider question, and it follows the `next` link to
     * the end because "is this network muted at all" cannot be answered from one page. The
     * alert type filters ask which mutes cover a type rather than which name it, so a
     * network-wide mute comes back under every value of both - see {@link MuteFilters} - while
     * the caller needs `read` on the `mute` resource type, which is a different privilege
     * from reading alerts, so a 403 here is not a bug.
     */
    listMutes(networkId: string, filters: MuteFilters = {}): Promise<Mute[]> {
        return this.listMutesPage(networkId, filters).then((page) => this.drain(page, 1));
    }

    /**
     * One page of mutes, and the link to the next.
     *
     * The page a listing view wants; {@link listMutes} is the same read followed to the end,
     * for callers that have to reason over the whole set. `size` is the page size the
     * service is asked for, and omitting it uses the service's own default.
     */
    listMutesPage(networkId: string, filters: MuteFilters = {}, size?: number, sort?: MuteSort): Promise<MutePage> {
        let params = this.toMuteParams(networkId, filters);
        if (!isNil(size)) {
            params = params.set('size', `${size}`);
        }
        params = withSort(params, sort);
        return firstValueFrom(this.http.get(`${this.apiUrl}/mutes`, { params }).pipe(timeout(READ_TIMEOUT_MS))).then(
            (body) => this.toMutePage(body)
        );
    }

    /**
     * The next page of mutes, from a `next` href handed back by a previous call.
     *
     * Resolved against the service base, then used as received — the same rule alerts
     * page by, and for the same reason. See {@link resolveHref}.
     */
    listMutesByHref(href: string): Promise<MutePage> {
        return firstValueFrom(this.http.get(this.resolveHref(href)).pipe(timeout(READ_TIMEOUT_MS))).then((body) =>
            this.toMutePage(body)
        );
    }

    /**
     * One mute, by id - how a caller turns an alert's `muted.by` into a person.
     *
     * Resolves a mute in any state, which is why this and not a filtered listing is the
     * right call: an alert names the mute that covered it when it was read, and that mute
     * can lapse a moment later. No `networkId`, because the network is derived from the
     * mute, and a mute in a network the caller may not see answers 404 - deliberately
     * indistinguishable from no such mute, and needing `read` on the `mute` resource type, so a
     * 403 here is not a bug.
     */
    getMute(muteId: string): Promise<Mute> {
        return firstValueFrom(this.http.get<Mute>(`${this.apiUrl}/mutes/${muteId}`).pipe(timeout(READ_TIMEOUT_MS)));
    }

    /**
     * Takes ownership. The alert still fires, still expires, still resolves on its own.
     *
     * The request carries only a note, because who took the alert comes from the caller's
     * token - so an alert cannot be claimed on somebody else's behalf, and a caller the
     * platform cannot name is refused rather than recorded as anonymous.
     */
    claim(alertId: string, request: ClaimRequest = {}): Promise<AlertView> {
        return firstValueFrom(this.http.post<AlertView>(`${this.apiUrl}/alerts/${alertId}/claim`, request));
    }

    /** Hands the alert back. The claim is cleared entirely. */
    release(alertId: string): Promise<AlertView> {
        return firstValueFrom(this.http.delete<AlertView>(`${this.apiUrl}/alerts/${alertId}/claim`));
    }

    /**
     * Closes an alert by hand. RESOLVED is terminal, so a condition that is still true
     * opens a *new* alert on the next evaluation. Resolving an already closed alert
     * returns it unchanged rather than failing, which matters when two people are
     * looking at the same console during an incident.
     */
    resolve(alertId: string): Promise<AlertView> {
        return firstValueFrom(this.http.post<AlertView>(`${this.apiUrl}/alerts/${alertId}/resolve`, {}));
    }

    /**
     * Creates a mute. This is the only one of the three actions that stops a page.
     *
     * The body is {@link muteBody}, which is also what {@link validateMute} asks about, and the
     * caller needs `create` on the `mute` resource type - deliberately a different privilege
     * from reading alerts, so a 403 here is not a bug.
     */
    createMute(networkId: string, request: MuteRequest): Promise<Mute> {
        return firstValueFrom(this.http.post<Mute>(`${this.apiUrl}/mutes`, this.muteBody(networkId, request)));
    }

    /**
     * Whether {@link createMute} would be accepted, without creating anything.
     *
     * The same verb, path and body - `nf-validate` is the whole of the routing, and the body
     * comes from {@link muteBody} rather than being built a second time, because a form that
     * went green on a body nobody was going to send is worse than no check. Answers 200 and
     * a list of the fields that would be refused, empty when the request would be accepted,
     * so a refusal is never a status - and authorized identically, so a caller without `create`
     * on `mute` still gets a 403.
     */
    validateMute(networkId: string, request: MuteRequest): Promise<ValidationError[]> {
        return this.check(`${this.apiUrl}/mutes`, this.muteBody(networkId, request));
    }

    /** Whether {@link rescheduleMute} would be accepted. See {@link validateMute}. */
    validateMuteReschedule(muteId: string, ends: string | null): Promise<ValidationError[]> {
        return this.check(`${this.apiUrl}/mutes/${muteId}`, { ends }, 'PATCH');
    }

    /**
     * Ends a mute now, as a soft delete: `revokedAt` and `revokedByIdentityId` are stamped and
     * the mute stays listable under `REVOKED`.
     *
     * Nothing is removed on purpose, because a mute that was in force is the answer to "why did
     * nobody get paged for this", and who revoked it comes from the caller's token. Revoking
     * an already revoked mute re-stamps both, so the record names the last caller to press
     * it; the response is the revoked mute, and the caller needs `delete` on the `mute`
     * resource type.
     */
    revokeMute(muteId: string): Promise<Mute> {
        return firstValueFrom(this.http.delete<Mute>(`${this.apiUrl}/mutes/${muteId}`));
    }

    /**
     * Moves a mute's end, later or earlier, with `null` making it indefinite.
     *
     * Ending one in the past is not the same as revoking it - the mute lapsed, nobody
     * stopped it - so a caller that means "stop this now, and record that I did" wants
     * {@link revokeMute} instead. `ends` is sent explicitly, null included, because omitting
     * the key is not how the contract spells indefinite, and the caller needs `update` on the
     * `mute` resource type.
     */
    rescheduleMute(muteId: string, ends: string | null): Promise<Mute> {
        return firstValueFrom(this.http.patch<Mute>(`${this.apiUrl}/mutes/${muteId}`, { ends }));
    }

    /**
     * The first page of latency policies for a network.
     *
     * Only what has been configured: a network that has set nothing lists nothing and still
     * alerts on the shipped defaults, so an empty page means "nobody has changed anything
     * here" rather than "latency alerting is off". Newest first unless `sort` says
     * otherwise, because the service defaults to `createdAt,desc` and this layer sends
     * nothing rather than spelling that default out.
     */
    listLatencyPoliciesPage(
        networkId: string,
        filters: LatencyPolicyFilters = {},
        size?: number,
        sort?: LatencyPolicySort
    ): Promise<LatencyPolicyPage> {
        let params = this.toLatencyPolicyParams(networkId, filters);
        if (!isNil(size)) {
            params = params.set('size', `${size}`);
        }
        params = withSort(params, sort);
        return firstValueFrom(
            this.http.get(`${this.apiUrl}/latency-policies`, { params }).pipe(timeout(READ_TIMEOUT_MS))
        ).then((body) => this.toLatencyPolicyPage(body));
    }

    /**
     * The next page of policies, from a `next` href handed back by a previous call.
     *
     * Resolved against the service base, then used as received — the same rule alerts and
     * mutes page by, and for the same reason. See {@link resolveHref}.
     */
    listLatencyPoliciesByHref(href: string): Promise<LatencyPolicyPage> {
        return firstValueFrom(this.http.get(this.resolveHref(href)).pipe(timeout(READ_TIMEOUT_MS))).then((body) =>
            this.toLatencyPolicyPage(body)
        );
    }

    /**
     * One policy, by id.
     *
     * No `networkId`: the network is derived from the policy. A policy in a network the
     * caller may not see answers **404**, deliberately indistinguishable from no such
     * policy.
     */
    getLatencyPolicy(policyId: string): Promise<LatencyPolicy> {
        return firstValueFrom(
            this.http.get<LatencyPolicy>(`${this.apiUrl}/latency-policies/${policyId}`).pipe(timeout(READ_TIMEOUT_MS))
        );
    }

    /**
     * One policy, by the href a response gave for it - the link an alert carries as
     * `latencyPolicy`.
     *
     * Followed rather than turned back into an id and a path, because the service decides
     * where its own resources live; see {@link resolveHref}. 404 for a policy deleted since
     * the alert was read, and 404 for one in a network the caller may not see - deliberately
     * the same answer.
     */
    getLatencyPolicyByHref(href: string): Promise<LatencyPolicy> {
        return firstValueFrom(this.http.get<LatencyPolicy>(this.resolveHref(href)).pipe(timeout(READ_TIMEOUT_MS)));
    }

    /**
     * Sets a network's latency thresholds, or overrides them for one component.
     *
     * `componentIds` decides the scope - empty is the network policy, and one or more ids
     * overrides it for that component - while how many and of what comes from the alert
     * type's `componentIdParts`, so the wrong count is a 400. Order does not matter, because
     * the service sorts the ids and joins them into the stored key, while a key already
     * covered is a 409 rather than a replacement - creating and updating are separate
     * privileges, and the response names the existing policy to patch.
     */
    createLatencyPolicy(networkId: string, request: LatencyPolicyRequest): Promise<LatencyPolicy> {
        return firstValueFrom(
            this.http.post<LatencyPolicy>(`${this.apiUrl}/latency-policies`, this.policyBody(networkId, request))
        );
    }

    /**
     * Whether {@link createLatencyPolicy} would be accepted, without creating anything.
     *
     * See {@link validateMute} for the shape of the answer. The rule worth knowing here is
     * the duplicate: a key already covered comes back as a fault on `$.alertType` where the
     * real create answers 409, and one store lookup feeds both.
     */
    validateLatencyPolicy(networkId: string, request: LatencyPolicyRequest): Promise<ValidationError[]> {
        return this.check(`${this.apiUrl}/latency-policies`, this.policyBody(networkId, request));
    }

    /** Whether {@link updateLatencyPolicy} would be accepted. See {@link validateMute}. */
    validateLatencyPolicyUpdate(policyId: string, thresholds: LatencyPolicyUpdate): Promise<ValidationError[]> {
        return this.check(`${this.apiUrl}/latency-policies/${policyId}`, this.oneKind(thresholds), 'PATCH');
    }

    /**
     * Moves one policy's thresholds.
     *
     * Thresholds only: what a policy applies to is its key, so the scope cannot be patched
     * and pointing a policy at something else means deleting it and creating another. The kind
     * is part of the thresholds and can be switched here - sending `fixed` to a policy that
     * held `auto` replaces it and clears the other field, and exactly one of the two goes out
     * (see {@link oneKind}) - under `update` on the `latency-policy` resource type, which is
     * a different privilege from `create`.
     */
    updateLatencyPolicy(policyId: string, thresholds: LatencyPolicyUpdate): Promise<LatencyPolicy> {
        return firstValueFrom(
            this.http.patch<LatencyPolicy>(`${this.apiUrl}/latency-policies/${policyId}`, this.oneKind(thresholds))
        );
    }

    /**
     * Removes a policy, restoring whatever sits under it.
     *
     * A real delete, unlike revoking a mute: a revoked mute is kept because it answers "why
     * did nobody get paged", while a policy that no longer applies suppressed nothing.
     * Nothing is ever left without an answer - removing an override restores the network
     * default, and removing that restores the shipped defaults - so latency alerting cannot
     * be switched off this way, which is what a mute is for.
     */
    deleteLatencyPolicy(policyId: string): Promise<void> {
        return firstValueFrom(this.http.delete<void>(`${this.apiUrl}/latency-policies/${policyId}`));
    }

    /** The first page of offline policies for a network. A network that has set nothing lists nothing and is graded on the fleet ladder. */
    listOfflinePoliciesPage(
        networkId: string,
        filters: OfflinePolicyFilters = {},
        size?: number,
        sort?: OfflinePolicySort
    ): Promise<OfflinePolicyPage> {
        let params = this.toOfflinePolicyParams(networkId, filters);
        if (!isNil(size)) {
            params = params.set('size', `${size}`);
        }
        params = withSort(params, sort);
        return firstValueFrom(
            this.http.get(`${this.apiUrl}/offline-policies`, { params }).pipe(timeout(READ_TIMEOUT_MS))
        ).then((body) => this.toOfflinePolicyPage(body));
    }

    /** The next page of offline policies, from a `next` href. See {@link resolveHref}. */
    listOfflinePoliciesByHref(href: string): Promise<OfflinePolicyPage> {
        return firstValueFrom(this.http.get(this.resolveHref(href)).pipe(timeout(READ_TIMEOUT_MS))).then((body) =>
            this.toOfflinePolicyPage(body)
        );
    }

    /** One offline policy, by id. A policy in a network the caller may not see is a 404. */
    getOfflinePolicy(policyId: string): Promise<OfflinePolicy> {
        return firstValueFrom(
            this.http.get<OfflinePolicy>(`${this.apiUrl}/offline-policies/${policyId}`).pipe(timeout(READ_TIMEOUT_MS))
        );
    }

    /** One offline policy, by the `offlinePolicy` link an alert carries. */
    getOfflinePolicyByHref(href: string): Promise<OfflinePolicy> {
        return firstValueFrom(this.http.get<OfflinePolicy>(this.resolveHref(href)).pipe(timeout(READ_TIMEOUT_MS)));
    }

    /** Sets a network's offline ladder for a component type, or overrides it for one component. A key already covered is a 409. */
    createOfflinePolicy(networkId: string, request: OfflinePolicyRequest): Promise<OfflinePolicy> {
        return firstValueFrom(
            this.http.post<OfflinePolicy>(`${this.apiUrl}/offline-policies`, this.offlinePolicyBody(networkId, request))
        );
    }

    /** Whether {@link createOfflinePolicy} would be accepted, without creating anything. See {@link validateMute}. */
    validateOfflinePolicy(networkId: string, request: OfflinePolicyRequest): Promise<ValidationError[]> {
        return this.check(`${this.apiUrl}/offline-policies`, this.offlinePolicyBody(networkId, request));
    }

    /** Whether {@link updateOfflinePolicy} would be accepted. See {@link validateMute}. */
    validateOfflinePolicyUpdate(policyId: string, update: OfflinePolicyUpdate): Promise<ValidationError[]> {
        return this.check(`${this.apiUrl}/offline-policies/${policyId}`, this.offlinePolicyUpdateBody(update), 'PATCH');
    }

    /** Replaces an offline policy's `enabled` flag and ladder. The key cannot change, and a missing ladder clears it. */
    updateOfflinePolicy(policyId: string, update: OfflinePolicyUpdate): Promise<OfflinePolicy> {
        return firstValueFrom(
            this.http.patch<OfflinePolicy>(
                `${this.apiUrl}/offline-policies/${policyId}`,
                this.offlinePolicyUpdateBody(update)
            )
        );
    }

    /** Removes an offline policy. The component falls back to the next level: the network default, then the fleet ladder. */
    deleteOfflinePolicy(policyId: string): Promise<void> {
        return firstValueFrom(this.http.delete<void>(`${this.apiUrl}/offline-policies/${policyId}`));
    }

    /**
     * The body `POST /mutes` takes, and the one its pre-submit check takes.
     *
     * One builder for both doors, because the check is only worth having while it is about
     * the request that will actually be sent, and `networkId` is a field of the body rather
     * than a query parameter so a form can attach a message to `$.networkId`. Incomplete
     * matchers are dropped here rather than sent, since every matcher must hold for a mute
     * to cover an alert and losing one widens the mute to everything the rest allow - while
     * `ends` is sent explicitly as null and there is no `starts`, because a mute begins when
     * the service stamps it.
     */
    private muteBody(networkId: string, request: MuteRequest): Record<string, any> {
        return {
            networkId,
            matchers: (request.matchers || []).filter((matcher) => !isEmpty(matcher.label) && !isEmpty(matcher.value)),
            alertTypes: request.alertTypes || [],
            ends: request.ends ?? null,
            reason: request.reason,
        };
    }

    /**
     * The body `POST /latency-policies` takes, and the one its pre-submit check takes.
     *
     * One builder for both doors, for the reason {@link muteBody} gives, and `networkId` is
     * a field here too. Blanks are dropped and the key left off entirely when nothing
     * survives, because the absence of a component is what a network policy is and an array
     * of empty strings is a different request.
     */
    private policyBody(networkId: string, request: LatencyPolicyRequest): Record<string, any> {
        const body: Record<string, any> = {
            networkId,
            alertType: request.alertType,
            ...this.oneKind(request),
        };
        const componentIds = (request.componentIds || []).map((id) => (id || '').trim()).filter((id) => !isEmpty(id));
        if (componentIds.length > 0) {
            body.componentIds = componentIds;
        }
        return body;
    }

    /** The body `POST /offline-policies` takes. Blank ids are dropped, and no ids at all is the network default. */
    private offlinePolicyBody(networkId: string, request: OfflinePolicyRequest): Record<string, any> {
        const body: Record<string, any> = {
            networkId,
            componentType: request.componentType,
            ...this.offlinePolicyUpdateBody(request),
        };
        if (!isNil(request.role)) {
            body.role = request.role;
        }
        const componentIds = (request.componentIds || []).map((id) => (id || '').trim()).filter((id) => !isEmpty(id));
        if (componentIds.length > 0) {
            body.componentIds = componentIds;
        }
        return body;
    }

    /** `enabled` and the ladder. Blank rungs are dropped, and a ladder with no rung left is sent as null so it inherits. */
    private offlinePolicyUpdateBody(update: OfflinePolicyUpdate): OfflinePolicyUpdate {
        const ladder: OfflineRungs = {};
        for (const rung of ['info', 'minor', 'major', 'critical'] as (keyof OfflineRungs)[]) {
            const value = (update.ladder?.[rung] || '').trim();
            if (!isEmpty(value)) {
                ladder[rung] = value;
            }
        }
        return { enabled: update.enabled === true, ladder: isEmpty(ladder) ? null : ladder };
    }

    /**
     * Asks whether a body would be accepted, and writes nothing.
     *
     * The header is the whole of the routing - same verb, same path - so this takes the url
     * and body already built for the real call and adds one header. A refusal is 200 here
     * and the list of fields is the answer, while only a genuine failure rejects: a 403, or
     * a network fault, both left to the caller rather than flattened into an empty list, and
     * timed out on the read timeout, because a check that never settles would leave a field
     * spinning.
     */
    private check(url: string, body: any, method: 'POST' | 'PATCH' = 'POST'): Promise<ValidationError[]> {
        const options = { headers: new HttpHeaders().set('nf-validate', 'true') };
        const sent =
            method === 'POST'
                ? this.http.post<ValidationError[]>(url, body, options)
                : this.http.patch<ValidationError[]>(url, body, options);
        return firstValueFrom(sent.pipe(timeout(READ_TIMEOUT_MS))).then((errors) => errors || []);
    }

    /**
     * One query string for both the listing and the stats.
     *
     * Repeatable parameters are appended, not set: `status=FIRING&status=PENDING` is how
     * the service reads a multi-select. Labels are free-form `label.<name>` pairs
     * because `labels` is a flattened field — a detector can add a dimension without a
     * mapping migration, so filtering on one must not need a release here either.
     */
    private toParams(networkId: string, filters: AlertFilters): HttpParams {
        let params = new HttpParams().set('networkId', networkId);
        for (const status of filters.status || []) {
            params = params.append('status', status);
        }
        for (const severity of filters.severity || []) {
            params = params.append('severity', severity);
        }
        for (const alertType of filters.alertType || []) {
            params = params.append('alertType', alertType);
        }
        if (!isNil(filters.claimed)) {
            params = params.set('claimed', `${filters.claimed}`);
        }
        if (!isNil(filters.muted)) {
            params = params.set('muted', `${filters.muted}`);
        }
        /* Two query parameters, not four. The service takes `openedAt` and
         * `firstAssertedAt`, each repeatable, with the comparison carried in the value —
         * `?firstAssertedAt=>=…&firstAssertedAt=<…`. There is no `openedAfter`, and an
         * unknown query parameter binds to nothing and is dropped without an error, so a
         * misspelled name returns an unfiltered answer that looks filtered. */
        for (const value of comparisons(filters.openedAfter, filters.openedBefore)) {
            params = params.append('openedAt', value);
        }
        for (const value of comparisons(filters.firstAssertedAfter, filters.firstAssertedBefore)) {
            params = params.append('firstAssertedAt', value);
        }
        for (const componentType of filters.componentType || []) {
            params = params.append('componentType', componentType);
        }
        for (const componentId of filters.componentId || []) {
            params = params.append('componentId', componentId);
        }
        /* Not a label filter. The service resolves this against the network's Ziti
         * controller and filters on the ids that match, so it can answer 400 (too short,
         * a rejected character, or more than 1000 matches) or 503 (the controller could
         * not be reached). A caller must not degrade the 503 into an unfiltered page. */
        if (!isEmpty(filters.componentName)) {
            params = params.set('componentName', filters.componentName as string);
        }
        for (const [name, value] of Object.entries(filters.labels || {})) {
            if (name && value) {
                params = params.set(`label.${name}`, value);
            }
        }
        return params;
    }

    /**
     * The query string for a mute listing.
     *
     * Repeatable parameters are appended, exactly as on {@link toParams}. `state` is always
     * sent, even when the caller named none, because the service defaults it to `ACTIVE` and
     * on this endpoint an absent `state` is the narrowest listing rather than the widest; a
     * blank `alertTypePrefix` is dropped rather than sent, since the service refuses one and a
     * cleared filter is an absent key.
     */
    private toMuteParams(networkId: string, filters: MuteFilters): HttpParams {
        let params = new HttpParams().set('networkId', networkId);
        const states = isEmpty(filters.state) ? ['ACTIVE'] : (filters.state as MuteState[]);
        for (const state of states) {
            params = params.append('state', state);
        }
        for (const alertType of filters.alertType || []) {
            params = params.append('alertType', alertType);
        }
        for (const prefix of filters.alertTypePrefix || []) {
            if (!isEmpty(prefix)) {
                params = params.append('alertTypePrefix', prefix);
            }
        }
        for (const identityId of filters.createdByIdentityId || []) {
            params = params.append('createdByIdentityId', identityId);
        }
        return params;
    }

    /**
     * Exactly one of `auto` and `fixed`, as the service insists on.
     *
     * Neither is the mistake to watch for, because a policy with no thresholds grades
     * nothing and the service answers 400 rather than storing one; both is a 400 as well.
     * `auto` wins when a caller somehow holds both, and the other key is left off rather
     * than sent as null - on `PATCH` a present key is a value being written, and writing null
     * to the kind that is staying would clear the policy this call is editing.
     */
    private oneKind(thresholds: LatencyPolicyUpdate): LatencyPolicyUpdate {
        if (!isNil(thresholds.auto)) {
            return { auto: thresholds.auto };
        }
        if (!isNil(thresholds.fixed)) {
            return { fixed: thresholds.fixed };
        }
        return {};
    }

    /**
     * The query string for a latency policy listing.
     *
     * Repeatable parameters are appended, exactly as on {@link toParams}, and every filter
     * here widens when it is absent - the opposite of `state` on the mute listing.
     * `componentType` does not exclude the network defaults, so a caller wanting the overrides
     * alone has to send `networkDefault=false` as well - and nothing here can express "the
     * defaults, of this component type", because a network default has no component.
     */
    private toLatencyPolicyParams(networkId: string, filters: LatencyPolicyFilters): HttpParams {
        let params = new HttpParams().set('networkId', networkId);
        for (const alertType of filters.alertType || []) {
            params = params.append('alertType', alertType);
        }
        for (const componentType of filters.componentType || []) {
            params = params.append('componentType', componentType);
        }
        for (const componentId of filters.componentId || []) {
            params = params.append('componentId', componentId);
        }
        for (const kind of filters.kind || []) {
            params = params.append('kind', kind as LatencyPolicyKind);
        }
        if (!isNil(filters.networkDefault)) {
            params = params.set('networkDefault', `${filters.networkDefault}`);
        }
        return params;
    }

    /** The query string for an offline policy listing. Repeatable parameters are appended. */
    private toOfflinePolicyParams(networkId: string, filters: OfflinePolicyFilters): HttpParams {
        let params = new HttpParams().set('networkId', networkId);
        for (const componentType of filters.componentType || []) {
            params = params.append('componentType', componentType);
        }
        for (const role of filters.role || []) {
            params = params.append('role', role);
        }
        for (const componentId of filters.componentId || []) {
            params = params.append('componentId', componentId);
        }
        if (!isNil(filters.networkDefault)) {
            params = params.set('networkDefault', `${filters.networkDefault}`);
        }
        return params;
    }

    /**
     * A `next` href as a url this console can actually GET.
     *
     * The links come back relative, because the alerting service builds them from its own
     * request path and knows nothing about the `/alerting/v1` prefix the gateway puts in
     * front of it - and handing one to HttpClient sends it to the console's own origin,
     * which answers 200 with the index page. Three shapes are handled: absolute, used as it
     * stands; path relative, joined to the base; and root relative, joined to the base as
     * well, keeping a service path it already carries rather than getting a second copy - none
     * of which parses the cursor, since only the origin and path are touched and the query is
     * carried across whole.
     */
    private resolveHref(href: string): string {
        if (/^[a-z][a-z0-9+.-]*:\/\//i.test(href)) {
            return href;
        }
        const base = new URL(this.apiUrl);
        /* '' when the service sits at the root of its host, which keeps the join below
         * from producing a double slash. */
        const basePath = base.pathname.replace(/\/$/, '');
        const resolved = new URL(href, `${base.origin}${basePath}/`);
        if (basePath && !resolved.pathname.startsWith(`${basePath}/`)) {
            return `${base.origin}${basePath}${resolved.pathname}${resolved.search}`;
        }
        return resolved.toString();
    }

    /**
     * Follows a mute listing's `next` links and returns every row from here on.
     *
     * Sequential rather than parallel, because a cursor is only known once the page before
     * it has answered, and it stops at {@link MUTE_PAGE_LIMIT} pages, since a service that
     * answered every page with a link to another would otherwise loop forever and the callers
     * of this are decorations that must not hang the view they sit on.
     *
     * @param page the page already read
     * @param pagesRead how many pages that is, so the limit counts the whole walk
     */
    private drain(page: MutePage, pagesRead: number): Promise<Mute[]> {
        if (isEmpty(page.nextHref) || pagesRead >= MUTE_PAGE_LIMIT) {
            return Promise.resolve(page.mutes);
        }
        return this.listMutesByHref(page.nextHref as string).then((next) =>
            this.drain(next, pagesRead + 1).then((rest) => [...page.mutes, ...rest])
        );
    }

    /** The mutes on one page, and the link to the next. */
    private toMutePage(body: any): MutePage {
        return {
            mutes: this.toCollection<Mute>(body, 'muteList'),
            nextHref: get(body, '_links.next.href'),
        };
    }

    /**
     * The policies on one page, the link to the next, and how many match in all.
     *
     * `totalElements` is carried through because the header is worth showing, and left
     * out of every "is there more" decision: the `next` link answers that, and a count
     * compared against rows on screen would be a guess that a client-side filter or a
     * concurrent write makes wrong.
     */
    private toLatencyPolicyPage(body: any): LatencyPolicyPage {
        return {
            policies: this.toCollection<LatencyPolicy>(body, 'latencyPolicyList'),
            nextHref: get(body, '_links.next.href'),
            totalElements: get(body, 'totalElements'),
        };
    }

    /** The offline policies on one page, the link to the next, and how many match in all. */
    private toOfflinePolicyPage(body: any): OfflinePolicyPage {
        return {
            policies: this.toCollection<OfflinePolicy>(body, 'offlinePolicyList'),
            nextHref: get(body, '_links.next.href'),
            totalElements: get(body, 'totalElements'),
        };
    }

    /**
     * The alerts and the mutes silencing them, from one response.
     *
     * `_embedded` carries both relations side by side: `alertViewList` and `muteList`.
     * The mutes are deduplicated by the service and cost it no extra query — the active
     * mute set is already loaded to decide muting — so a caller never has to turn a page
     * of `muted.by` ids into mutes with a request per id.
     */
    private toPage(body: any): AlertPage {
        return {
            alerts: this.toCollection<AlertView>(body, 'alertViewList'),
            mutes: this.toCollection<Mute>(body, 'muteList'),
            nextHref: get(body, '_links.next.href'),
        };
    }

    /**
     * HAL responses carry the collection under `_embedded.<typeList>` and omit
     * `_embedded` entirely when empty, so a missing key is an empty page rather than an
     * error. A bare array is accepted too, which keeps test fixtures simple.
     */
    private toCollection<T>(body: any, embeddedKey: string): T[] {
        if (Array.isArray(body)) {
            return body as T[];
        }
        return get(body, ['_embedded', embeddedKey], []) as T[];
    }
}
